import { Component, Input, Type, OnInit, ChangeDetectorRef, forwardRef } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { DistributionService } from '../../../TargetingShared/services/distribution.service';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';
import { FormFieldModel } from '../../../PageBuilder/models/form-field.model';
import { DistributionSummaryModel } from '../../../TargetingShared/model/distribution-summary-model';
import { WireService } from '../../../../Amplify/Wire/services/wire.service';
import { WireListItemModel } from '../../../../Amplify/Wire/models/wire-list-item.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { chartColors } from '../../../Charts/enums/chart-colors.enum';
import { PREventTypeEnum } from '../../enums/pr-event-type.enum';
import { PREventModel } from '../../models/pr-event.model';
import { ScheduleType } from '../../../Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { TimeModel } from '../../../../Common/Forms/models/time.model';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { MinuteModel } from '../../../../Common/Forms/models/minute.model';
import { meridiansEnum } from '../../../../Common/Forms/enums/meridians.enum';
import { TwelveHourModel } from '../../../../Common/Forms/models/twelve-hour.model';

export class PREventsFieldModel implements FormFieldModel {
    fieldComponent: Type<AbstractFormFieldComponent>;
    property: string;
    label: string;
    description: string;

    constructor(
        fieldComponent: Type<AbstractFormFieldComponent>,
        property: string,
        label: string,
        description?: string,
    ) {
        this.fieldComponent = fieldComponent;
        this.property = property;
        this.label = label;
        this.description = description;
    }
}



@Component({
    selector: 'agpr-pr-events-field',
    templateUrl: './pr-events-field.component.html',
    styleUrls: ['./pr-events-field.component.scss'],
    standalone: false
})
export class PREventsFieldComponent extends AbstractFormFieldComponent implements OnInit {
    readonly prEventTypeEnum = PREventTypeEnum;
    readonly buttonTypesEnum = ButtonTypesEnum;

    readonly maxEventCount = 25;

    readonly eventTypeOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Email Distribution', PREventTypeEnum.emailDistribution),
        new DropdownOptionModel('Newswire', PREventTypeEnum.newswire),
        new DropdownOptionModel('Custom', PREventTypeEnum.custom)
    ];

    events: PREventModel[];
    @Input() declare value: PREventModel[];
    eventTime: TimeModel;
    suggestions = {
        [PREventTypeEnum.emailDistribution]: new BehaviorSubject<DistributionSummaryModel[]>([]),
        [PREventTypeEnum.newswire]: new BehaviorSubject<WireListItemModel[]>([]),
        [PREventTypeEnum.custom]: new BehaviorSubject([])
    };

    // @NOTE dependency on distributions and wire from Reports
    constructor(
        private distributionService: DistributionService,
        private wireService: WireService,
        private localizationService: LocalizationService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        super();

        this.distributionService.getDistributionSummaries().subscribe(distributions => {
            if (distributions) {
                this.suggestions[PREventTypeEnum.emailDistribution].next(distributions);
            }
        });

        this.wireService.all$.subscribe(wires => {
            if (wires) {
                this.suggestions[PREventTypeEnum.newswire].next(wires);
            }
        });
    }

    ngOnInit(): void {
        if (this.value) {
            this.events = this.value;
        }

        if (!this.events || this.events.length === 0) {
            this.events = [];

        } else {
            this.events.forEach(event => {
                if (event.date) {
                    event.date.absoluteDate = new Date(event.date.absoluteDate);
                    const hours = event.date.absoluteDate.getHours();
                    event.time = new TimeModel(timeFormatsEnum.twelveHour, hours as TwelveHourModel,
                        event.date.absoluteDate.getMinutes() as MinuteModel,
                        ((hours >= 12) ? meridiansEnum.PM : meridiansEnum.AM), null, 0);
                }
            });
        }

        this.emitChanges();
    }

    handleEventTypeSelection(event: PREventModel, type: PREventTypeEnum) {
        if (event.type !== type) {
            // unset the selected event when switching types
            event.model = null;
            event.date = new DateQueryModel();
            event.date.absoluteDate = new Date();
            event.name = '';
        }

        event.type = type;
        this.emitChanges();
    }

    handleEventSelection(event: PREventModel): void {
        if (event.type === PREventTypeEnum.newswire && event.model) {
            this.wireService.getById(event.model.id).subscribe(wire => {
                if (wire.deliverySchedule.scheduleType !== ScheduleType.immediate) {
                    event.date = new DateQueryModel();
                    event.date.type = DateQueryTypeEnum.ABSOLUTE;
                    event.date.absoluteDate = new Date(wire.deliverySchedule.scheduledDateUTC);
                }

                event.name = wire.name;
                event.typeLabel = this.localizationService.transform('Reports.prevents.wireType');
            });
        } else if (event.type === PREventTypeEnum.emailDistribution && event.model) {
            this.distributionService.getDistributionById(event.model.id).subscribe(distribution => {
                if (distribution.scheduledDate) {
                    event.date = new DateQueryModel();
                    event.date.type = DateQueryTypeEnum.ABSOLUTE;
                    event.date.absoluteDate = new Date(distribution.scheduledDate);
                }

                event.name = distribution.name;
                event.typeLabel = this.localizationService.transform('Reports.prevents.distributionType');
            });
        } else if (event.type === PREventTypeEnum.custom) {
            event.name = '';
            event.date = new DateQueryModel();
            event.date.type = DateQueryTypeEnum.ABSOLUTE;
            event.date.absoluteDate = new Date();
            event.typeLabel = this.localizationService.transform('Reports.prevents.customType');
        }

        this.emitChanges();
    }

    handleColorChange(event: PREventModel, color: string): void {
        event.color = color;
        this.emitChanges();
    }

    addEvent(event?: any): void {
        const date = new Date();
        date.setHours(12);
        date.setMinutes(0);

        this.events.push({
            name: '',
            date: {
                type: DateQueryTypeEnum.ABSOLUTE,
                absoluteDate: date
            },
            model: null,
            color: chartColors.agprLightBlue,
            type: PREventTypeEnum.custom,
            typeLabel: '',
            time: new TimeModel(timeFormatsEnum.twelveHour, date.getHours() as TwelveHourModel, date.getMinutes() as MinuteModel, meridiansEnum.PM)
        });

        this.emitChanges();
    }

    deleteEvent(event: PREventModel): void {
        this.events.splice(this.events.indexOf(event), 1);
        this.emitChanges();
    }

    emitChanges(): void {
        this.value = this.events;
        this.changeDetectorRef.detectChanges();
        this.changes.emit(this.value);
    }

    eventTimeChanged(event: PREventModel): void {
        if (event.time.meridian === meridiansEnum.PM) {
            event.date.absoluteDate.setHours(event.time.hour + 12);
        } else {
            event.date.absoluteDate.setHours(event.time.hour);
        }
        event.date.absoluteDate.setMinutes(event.time.minute);
        this.emitChanges();
    }


    openCalendar(event, calendar): void {
        calendar.hideOverlay();
        calendar.showOverlay();
        event.stopPropagation();
    }
}
